import React, { useState, useEffect } from 'react';
import {
  ExpandMore
} from "@mui/icons-material";
import { Accordion, AccordionSummary, AccordionDetails, Divider } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterItem,
  CharacterArmor,
  SpellLimitation,
  CharacterBasicItem,
  CharacterAmmunition,
  CharacterWeapon,
  CharacterGem,
  Campaign,
} from "@dndModels";
import { 
  ALL_DRAGONS, 
  CREATURE_TYPES, 
  RARITY_NUM_TO_STR_MAP,
} from "@dndModels/Constants";
import { DisablableOption } from "@models";

import DataUtilities from '@dndUtilities/data_utilities';
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import StringBox from "@components/input/strings/StringBox";

import ItemDetails from "@dndComponents/equipment/ItemDetails";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CharacterItemMIK from "@dndComponents/character/inventory/item/CharacterItemMIK";
import SelectSpellBox from "@dndComponents/select/SelectSpellBox";
import SpellDetails from "@dndComponents/display/SpellDetails";
import CharacterItemOptionControls from "@dndComponents/character/inventory/item/CharacterItemOptionControls";
import CharacterItemSellingControls from "@dndComponents/character/inventory/item/CharacterItemSellingControls";
import StoredSpellsControls from "@dndComponents/character/inventory/StoredSpellsControls";
import SpellStoringItemControls from "@dndComponents/character/inventory/SpellStoringItemControls";
import SelectObjectBox from '@components/input/selects/SelectObjectBox';
import FilterBox from '@dndComponents/filtering/FilterBox';
import CharacterItemUpgradeControls from './item/CharacterItemUpgradeControls';

interface Props {
  char: Character | Campaign;
  char_item: CharacterItem;
  // onChange: (repopulate: boolean, close: boolean) => void;
  onRemove: () => void;
}

// const api = API.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterNonCustomItemControls({
  char,
  char_item,
  // onChange,
  onRemove
}: Props) {
  const [selling, setSelling] = useState(false);
  const [buying, setBuying] = useState(false);
  const [ping, setPing] = useState(false);
  const [show_details, setShowDetails] = useState(false);
  const [show_spell, setShowSpell] = useState(true);

  const renderCustomizations = () => {
    return (
      <Grid
        size={12}
        className="header padded">
        <Accordion>
          <AccordionSummary 
            style={{
              fontWeight: "bold",
              height: "30px"
            }}
            expandIcon={<ExpandMore />}>
            Customize
          </AccordionSummary>
          <AccordionDetails>
            <div style={{
              width: "100%"
            }}>
              <Grid container spacing={0} direction="row">
                <Grid size={12}>
                  <StringBox
                    name="Name"
                    value={ char_item._name }
                    onBlur={(changed: string) => {
                      char_item._name = changed;
                      // onChange(false, false);
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={6}>
                  <StringBox
                    name="Cost Override (gp)"
                    value={ char_item.customizations.cost ? `${char_item.customizations.cost}` : "" }
                    type="number"
                    onBlur={(changed: string) => {
                      if (changed === "") {
                        delete char_item.customizations.cost;
                      } else {
                        try {
                          char_item.customizations.cost = +changed;
                        } catch {}
                      }
                      // onChange(false, false);
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={6}>
                  <StringBox
                    name="Weight Override (lb)"
                    value={ char_item.customizations.weight ? `${char_item.customizations.weight}` : "" }
                    type="number"
                    onBlur={(changed: string) => {
                      if (changed === "") {
                        delete char_item.customizations.weight;
                      } else {
                        try {
                          char_item.customizations.weight = +changed;
                        } catch {}
                      }
                      // onChange(false, false);
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <StringBox
                    name="Notes"
                    multiline
                    value={ char_item.customizations.notes ? `${char_item.customizations.notes}` : "" }
                    onBlur={(changed: string) => {
                      if (changed === "") {
                        delete char_item.customizations.notes;
                      } else {
                        try {
                          char_item.customizations.notes = changed;
                        } catch {}
                      }
                      // onChange(false, false);
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                { char_item instanceof CharacterArmor &&
                  <>
                    <Grid size={12}>
                      <StringBox
                        name="Override AC"
                        value={ char_item.AC_override ? `${char_item.AC_override}` : "" }
                        type="number"
                        onBlur={(changed: string) => {
                          if (changed === "") {
                            char_item.AC_override = null;
                          } else {
                            try {
                              char_item.AC_override = +changed;
                            } catch {}
                          }
                          // onChange(false, false);
                          char.changed();
                          char.subscription_holder.itemsChanged();
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <StringBox
                        name="Modify AC"
                        value={ char_item.AC_modifier ? `${char_item.AC_modifier}` : "" }
                        type="number"
                        onBlur={(changed: string) => {
                          if (changed === "") {
                            char_item.AC_modifier = 0;
                          } else {
                            try {
                              char_item.AC_modifier = +changed;
                            } catch {}
                          }
                          // onChange(true, false);
                          char.changed();
                          char.subscription_holder.itemsChanged();
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                  </>
                }
              </Grid>
            </div>
          </AccordionDetails>
        </Accordion>
      </Grid>
    );
  }

  const renderUpgrades = () => {
    return (
      <CharacterItemUpgradeControls 
        char={char}
        char_item={char_item}
      />
    );
  };

  const renderExtraSelect = () => {
    const base_item = char_item.base_item;
    if (base_item) {
      let rarity_select: any = null;
      // debugger;
      if (base_item.min_rarity < base_item.max_rarity) {
        const options: DisablableOption[] = [];
        for (let i = base_item.min_rarity; i <= base_item.max_rarity; ++i) {
          let rarity_for_opt = char_item.rarity;
          if (char_item.use_selected_rarity > -1) {
            rarity_for_opt -= char_item.use_selected_rarity;
          }
          rarity_for_opt += i;
          const opt: DisablableOption = { id: `${i}`, text: RARITY_NUM_TO_STR_MAP[i] };
          if (rarity_for_opt > 5) {
            opt.disabled = true;
            opt.reason = "Pushes full Rarity past Legendary";
          }
          options.push(opt);
        }
        rarity_select = (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Select Base Rarity"
              options={options}
              value={`${char_item.use_selected_rarity}`}
              onChange={(changed: string) => {
                char_item.selected_rarity = +changed;
                // onChange(true, false);
                char.changed();
                char.subscription_holder.itemsChanged();
                setPing(!ping);
              }}
            />
          </Grid>
        );
      } else if (char_item.varying_mik) {
        const varying_mik = char_item.varying_mik;
        const options: DisablableOption[] = [];
        for (let i = varying_mik.min_rarity; i <= varying_mik.max_rarity; ++i) {
          let rarity_for_opt = char_item.rarity;
          if (char_item.use_selected_rarity > -1) {
            rarity_for_opt -= char_item.use_selected_rarity;
          }
          rarity_for_opt += i;
          const opt: DisablableOption = { id: `${i}`, text: RARITY_NUM_TO_STR_MAP[i] };
          if (rarity_for_opt > 5) {
            opt.disabled = true;
            opt.reason = "Pushes full Rarity past Legendary";
          }
          options.push(opt);
        }
        rarity_select = (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name={`Select Rarity for ${varying_mik.name}`}
              options={options}
              value={`${char_item.use_selected_rarity}`}
              onChange={(changed: string) => {
                char_item.selected_rarity = +changed;
                // onChange(true, false);
                char.changed();
                char.subscription_holder.itemsChanged();
                setPing(!ping);
              }}
            />
          </Grid>
        );
      }
      const extra_mik = char_item.extra_mik;
      const extra_base_type = extra_mik && extra_mik.source ? extra_mik.source.extra_base_type : base_item.extra_base_type;
      if (extra_base_type === "" || extra_base_type === "None") {
        if (rarity_select) {
          return (
            <Grid container spacing={0} direction="row">{ rarity_select }</Grid>
          );
        }
        return null;
      } else if (extra_base_type === "Spell") {
        // if (extra_mik && extra_mik.source) {
        //   const mik = extra_mik.source;
        //   const limits = mik.extra_base_limit;
        //   const all_spells: Spell[] = [];
        //   if (limits.limits.length > 0) {
        //     for (const limit of limits.limits) {
        //       if (limit instanceof SpellLimitation) {
        //         const spells = data_util.getSpells({ limit });
        //         const all_spell_ids = all_spells.map(o => o._id);
        //         all_spells.push(...spells.filter(o => !all_spell_ids.includes(o._id)));
        //       }
        //     }
        //   } else {
        //     all_spells.push(...data_util.spells);
        //   }
        // }
        // const extra_base_limits = extra_mik && extra_mik.source ? extra_mik.source.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] } : base_item.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
        // let spell_levels: number[] = [];
        // // debugger;
        // if (extra_base_limits.levels.includes("Any")) {
        //   spell_levels = [0,1,2,3,4,5,6,7,8,9];
        // } else {
        //   spell_levels = extra_base_limits.levels.map(o => {
        //     if (o === "Cantrip") {
        //       return 0;
        //     } else {
        //       return +o;
        //     }
        //   });
        // }
        // const spells = data_util.spells.filter(o => 
        //   spell_levels.includes(o.level) && 
        //   (extra_base_limits.schools.includes("Any") || 
        //     extra_base_limits.schools.includes(o.school))
        // );
        const spell_limit = extra_mik && extra_mik.source && extra_mik.source.extra_base_limit.limits.length > 0 && extra_mik.source.extra_base_limit.limits[0] instanceof SpellLimitation ? extra_mik.source.extra_base_limit.limits[0] : new SpellLimitation();
        // spell_limit.spell_ids = spells.map(o => o._id);
        debugger;
        return (
          <Grid container spacing={0} direction="row">
            { rarity_select }
            <Grid
              size={{
                xs: 12
              }}>
              <SelectSpellBox 
                name="Spell"
                value={char_item.extra}
                spell_limit={spell_limit}
                // specific_spell_ids={spells.map(o => o._id)}
                onChange={(changed: string) => {
                  const spell = data_util.spells.find(o => o._id === changed);
                  if (spell) {
                    char_item.extra = changed;
                    char_item.extra_spell = spell;
                    // onChange(true, false);
                    char.changed();
                    char.subscription_holder.itemsChanged();
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        let extra_options: string[] = [];
        const extra_base_limits = extra_mik && extra_mik.source ? extra_mik.source.extra_base_limits as string[] : base_item.extra_base_limits as string[];
        if (extra_base_type === "Dragon Breed") {
          extra_options = extra_base_limits.length > 0 ? extra_base_limits : ALL_DRAGONS;
        } else if (extra_base_type === "Creature Type") {
          extra_options = extra_base_limits.length > 0 ? extra_base_limits : CREATURE_TYPES;
        }
        return (
          <Grid container spacing={0} direction="row">
            { rarity_select }
            <Grid
              size={{
                xs: 12
              }}>
              <SelectStringBox
                name={extra_base_type}
                options={extra_options}
                value={char_item.extra}
                onChange={(changed: string) => {
                  char_item.extra = changed;
                  // onChange(true, false);
                  char.changed();
                  char.subscription_holder.itemsChanged();
                  setPing(!ping);
                }}
              />
            </Grid>
          </Grid>
        );
      }
    }
    return <></>;
  }

  const renderDetails = () => {
    return (
      <>
        <Grid size={12}>
          <ToggleButtonBox
            name="Show Details"
            value={show_details}
            onToggle={() => { setShowDetails(!show_details); }}
          />
        </Grid>
        { show_details && 
          <Grid size={12}>
            <ItemDetails character={char} item={char_item} />
          </Grid>
        }
      </>
    );
  }

  const renderSpellDetails = () => {
    const base_item = char_item.base_item;
    if (base_item) {
      const extra_mik = char_item.extra_mik;
      const extra_base_type = extra_mik && extra_mik.source ? extra_mik.source.extra_base_type : base_item.extra_base_type;
      if (extra_base_type === "Spell") {
        const extra_spell = data_util.spells.find(o => o._id === char_item.extra);
        if (extra_spell) {
          return (
            <>
              <Grid size={12}>
                <ToggleButtonBox
                  name="Show Spell"
                  value={show_spell}
                  onToggle={() => { setShowSpell(!show_spell); }}
                />
              </Grid>
              { show_spell && 
                <Grid size={12}>
                  <SpellDetails spell={extra_spell} />
                </Grid>
              }
            </>
          );
        }
      }
    }
    return <></>;
  }

  const renderCount = () => {
    // console.log(char_item);
    if (char_item instanceof CharacterBasicItem || 
      char_item instanceof CharacterAmmunition || 
      char_item instanceof CharacterWeapon || 
      char_item instanceof CharacterGem) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Quantity"
            value={ `${char_item.count}` }
            type="number"
            onBlur={(changed: string) => {
              const new_obj = char_item;
              new_obj.count = +changed;
              // onChange(false, false);
              char.changed();
              char.subscription_holder.itemsChanged();
              setPing(!ping);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderSpellStoring = () => {
    const item = char_item.base_item;
    if (item && item.spell_storing && char instanceof Character) {
      return (
        <StoredSpellsControls
          char={char}
          char_item={char_item}
          item={item}
        />
      );
    }
    return null;
  }

  const renderSpellStoringItem = () => {
    if (char instanceof Character) {
      return (
        <Grid
          className="header padded"
          size={{
            xs: 12
          }}>
          <Accordion>
            <AccordionSummary 
              style={{
                fontWeight: "bold",
                height: "30px"
              }}
              expandIcon={<ExpandMore />}>
              Spell-Storing Item
            </AccordionSummary>
            <AccordionDetails>
              <div style={{
                width: "100%"
              }}>
                <SpellStoringItemControls
                  character={char}
                  char_item={char_item}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        </Grid>
      );
    }
  }

  console.log("Rendering Char Non Custom");
  return (
    <Grid container spacing={0} direction="row">
      { renderCustomizations() }
      { renderUpgrades() }
      { renderSpellStoringItem() }
      { renderExtraSelect() }
      { renderDetails() }
      { renderSpellDetails() }
      { renderCount() }
      { renderSpellStoring() }
      <Grid size={12}>
        <CharacterItemOptionControls
          char={char}
          char_item={char_item}
          onChange={() => {
            char.changed();
            char.subscription_holder.itemsChanged();
            setPing(!ping);
          }}
          onSelling={() => { setSelling(!selling); }}
          onBuying={() => { setBuying(!buying); }}
          onRemove={onRemove}
        />
      </Grid>
      { selling ? 
        <Grid
          size={{
            xs: 12
          }}>
          <CharacterItemSellingControls
            char={char}
            char_item={char_item}
            onChange={() => {
              char.changed();
              char.subscription_holder.itemsChanged();
              setPing(!ping);
            }}
            onCancel={() => { setSelling(false); }}
          />
        </Grid>
      : buying &&
        <Grid
          size={{
            xs: 12
          }}>
          <CharacterItemSellingControls
            char={char}
            char_item={char_item}
            buying
            onChange={() => {
              char.changed();
              char.subscription_holder.itemsChanged();
              setPing(!ping);
            }}
            onCancel={() => { setBuying(false); }}
          />
        </Grid>
      }
    </Grid>
  );
}
